import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { officeExpenseAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import './OfficeExpenseCreate.css';

const OfficeExpenseEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    description: '',
    quantity: '',
    invoiceNumber: '',
    invoiceOwner: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadExpense();
  }, [id]);

  const loadExpense = async () => {
    try {
      setLoadingData(true);
      const response = await officeExpenseAPI.getById(id);
      const data = response.data;
      const dateStr = new Date(data.date).toISOString().split('T')[0];
      setFormData({
        ...data,
        date: dateStr,
        amount: data.amount || '',
        quantity: data.quantity || '',
      });
    } catch (err) {
      setErrorMessage(t('officeExpense.edit.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.date) {
      newErrors.date = t('officeExpense.edit.errors.dateRequired');
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = t('officeExpense.edit.errors.amountRequired');
    }
    if (!formData.description.trim()) {
      newErrors.description = t('officeExpense.edit.errors.descriptionRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        amount: parseFloat(formData.amount),
        quantity: formData.quantity ? parseInt(formData.quantity) : null,
      };
      await officeExpenseAPI.update(id, submitData);
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/office-expense');
      }, 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('officeExpense.edit.messages.updateError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('officeExpense.edit.fields.date')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.date ? 'is-invalid' : ''}`}
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                />
              </div>
              {errors.date && (
                <div className="error-message">{errors.date}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-dollar-sign"></i> {t('officeExpense.edit.fields.amount')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-dollar-sign input-icon"></i>
                <input
                  type="number"
                  step="0.01"
                  className={`form-input ${errors.amount ? 'is-invalid' : ''}`}
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder={t('officeExpense.edit.placeholders.amount')}
                />
              </div>
              {errors.amount && (
                <div className="error-message">{errors.amount}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-list"></i> {t('officeExpense.edit.fields.quantity')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-list input-icon"></i>
                <input
                  type="number"
                  className="form-input"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  placeholder={t('officeExpense.edit.placeholders.quantity')}
                />
            </div>
          </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-align-left"></i> {t('officeExpense.edit.fields.description')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-align-left input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.description ? 'is-invalid' : ''}`}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder={t('officeExpense.edit.placeholders.description')}
                  maxLength={500}
                />
              </div>
              {errors.description && (
                <div className="error-message">{errors.description}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-file-invoice"></i> {t('officeExpense.edit.fields.invoiceNumber')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-file-invoice input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleChange}
                  placeholder={t('officeExpense.edit.placeholders.invoiceNumber')}
                  maxLength={100}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {t('officeExpense.edit.fields.invoiceOwner')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="invoiceOwner"
                  value={formData.invoiceOwner}
                  onChange={handleChange}
                  placeholder={t('officeExpense.edit.placeholders.invoiceOwner')}
                  maxLength={200}
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => navigate('/office-expense')}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('common.loading') : t('officeExpense.edit.save')}
            </button>
          </div>
        </form>
      </div>

      <SuccessModal
        show={showSuccessModal}
        message={t('officeExpense.edit.messages.updateSuccess')}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default OfficeExpenseEdit;


